import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { TablePagination } from '../../components/ui/table-pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, Pencil, Trash2, Save, X, Building2, 
  CreditCard, CheckCircle, Clock, XCircle, Loader2,
  FileDown, Search, Package, RefreshCw, Eye, ArrowLeft
} from 'lucide-react';

const API = `${window.location.origin}/api`;

// Default Philippine banks and e-wallets
const DEFAULT_BANKS = [
  { name: 'Alipay Philippines', code: 'ALIPAY', account_format: 'Account Number' },
  { name: 'AllBank (A Thrift Bank), Inc.', code: 'ALLBANK', account_format: 'Account Number' },
  { name: 'Asia United Bank Corporation', code: 'AUB', account_format: 'Account Number' },
  { name: 'Bananapay Fintech Services', code: 'BANANAPAY', account_format: 'Mobile Number' },
  { name: 'Bangko Mabuhay (A Rural Bank), Inc.', code: 'BMRB', account_format: 'Account Number' },
  { name: 'Bank of China', code: 'BOC', account_format: 'Account Number' },
  { name: 'Bank of Commerce', code: 'BNKCOM', account_format: 'Account Number' },
  { name: 'Bank of the Philippine Islands / BPI Family', code: 'BPI', account_format: 'Account Number' },
  { name: 'BDO Network Bank', code: 'BDONB', account_format: 'Account Number' },
  { name: 'BDO Unibank, Inc.', code: 'BDO', account_format: 'Account Number' },
  { name: 'BPI Direct BanKo', code: 'BPIDB', account_format: 'Account Number' },
  { name: 'Camalig Bank, Inc. (A Rural Bank)', code: 'CAMALIG', account_format: 'Account Number' },
  { name: 'Cantilan Bank Inc.', code: 'CANTILAN', account_format: 'Account Number' },
  { name: 'CARD BANK Inc.', code: 'CARDBANK', account_format: 'Account Number' },
  { name: 'CARD SME Bank, Inc., A Thrift Bank', code: 'CARDSME', account_format: 'Account Number' },
  { name: 'Cebuana Lhuillier Rural Bank, Inc', code: 'CEBUANA', account_format: 'Account Number' },
  { name: 'China Bank Savings, Inc.', code: 'CBS', account_format: 'Account Number' },
  { name: 'China Banking Corporation', code: 'CHINABANK', account_format: 'Account Number' },
  { name: 'CIMB Philippines, Inc.', code: 'CIMB', account_format: 'Account Number' },
  { name: 'CIS Bayad Center, Inc.', code: 'BAYAD', account_format: 'Account Number' },
  { name: 'Community Rural Bank of Romblon, Inc.', code: 'CRBROMB', account_format: 'Account Number' },
  { name: 'CTBC Bank (Philippines) Corporation', code: 'CTBC', account_format: 'Account Number' },
  { name: 'DCPay (Coins.ph)', code: 'COINSPH', account_format: 'Mobile Number' },
  { name: 'Development Bank of the Philippines', code: 'DBP', account_format: 'Account Number' },
  { name: 'Dumaguete City Development Bank', code: 'DCDB', account_format: 'Account Number' },
  { name: 'Dungganon Bank (A Microfinance Rural Bank), Inc.', code: 'DUNGGANON', account_format: 'Account Number' },
  { name: 'East West Banking Corporation', code: 'EWB', account_format: 'Account Number' },
  { name: 'East West Rural Bank', code: 'EWRB', account_format: 'Account Number' },
  { name: 'Easy Pay Global EMI Corp', code: 'EASYPAY', account_format: 'Account Number' },
  { name: 'Entrepreneur Rural Bank', code: 'ERB', account_format: 'Account Number' },
  { name: 'Equicom Savings Bank, Inc.', code: 'EQUICOM', account_format: 'Account Number' },
  { name: 'GoTyme Bank Corporation', code: 'GOTYME', account_format: 'Account Number' },
  { name: 'GrabPay', code: 'GRABPAY', account_format: 'Mobile Number' },
  { name: 'G-Xchange, Inc.', code: 'GCASH', account_format: 'Mobile Number (09XXXXXXXXX)' },
  { name: 'The Hong Kong and Shanghai Banking Corporation Limited, Philippine Branch', code: 'HSBC', account_format: 'Account Number' },
  { name: 'INFOSERVE INCORPORATED', code: 'INFOSERVE', account_format: 'Account Number' },
  { name: 'ING Bank N.V.', code: 'ING', account_format: 'Account Number' },
  { name: 'I-Remit Inc.', code: 'IREMIT', account_format: 'Account Number' },
  { name: 'ISLA Bank (A Thrift Bank), Inc.', code: 'ISLA', account_format: 'Account Number' },
  { name: 'Land Bank of The Philippines', code: 'LBP', account_format: 'Account Number' },
  { name: 'Legazpi Savings Bank', code: 'LSB', account_format: 'Account Number' },
  { name: 'Lulu Financial Services', code: 'LULU', account_format: 'Account Number' },
  { name: 'Luzon Development Bank', code: 'LDB', account_format: 'Account Number' },
  { name: 'Malayan Bank Savings and Mortgage Bank, Inc.', code: 'MALAYAN', account_format: 'Account Number' },
  { name: 'MAYA BANK, INC', code: 'MAYABANK', account_format: 'Account Number' },
  { name: 'Maybank Philippines, Inc.', code: 'MAYBANK', account_format: 'Account Number' },
  { name: 'Metropolitan Bank and Trust Company', code: 'METROBANK', account_format: 'Account Number' },
  { name: 'Mindanao Consolidated Cooperative Bank', code: 'MCCB', account_format: 'Account Number' },
  { name: 'Omnipay, Inc.', code: 'OMNIPAY', account_format: 'Account Number' },
  { name: 'OWN BANK THE RURAL BANK OF CAVITE CITY INC.', code: 'OWNBANK', account_format: 'Account Number' },
  { name: 'Pacific Ace Savings Bank, Inc.', code: 'PACIFICACE', account_format: 'Account Number' },
  { name: 'Partner Rural Bank (Cotabato), Inc.', code: 'PARTNERRB', account_format: 'Account Number' },
  { name: 'PayMaya Philippines, Inc.', code: 'PAYMAYA', account_format: 'Mobile Number (09XXXXXXXXX)' },
  { name: 'Philippine Bank of Communications', code: 'PBCOM', account_format: 'Account Number' },
  { name: 'Philippine Business Bank', code: 'PBB', account_format: 'Account Number' },
  { name: 'Philippine Digital Asset Exchange', code: 'PDAX', account_format: 'Account Number' },
  { name: 'Philippine National Bank', code: 'PNB', account_format: 'Account Number' },
  { name: 'Philippine Savings Bank', code: 'PSBANK', account_format: 'Account Number' },
  { name: 'Philippine Veterans Bank', code: 'PVB', account_format: 'Account Number' },
  { name: 'PhilTrust Bank', code: 'PHILTRUST', account_format: 'Account Number' },
  { name: 'PPS-PEPP Financial Services Corporation', code: 'PPSPEPP', account_format: 'Account Number' },
  { name: 'Producers Savings Bank Corporation', code: 'PRODUCERS', account_format: 'Account Number' },
  { name: 'Queen City Development Bank, Inc.', code: 'QCDB', account_format: 'Account Number' },
  { name: 'Quezon Capital Rural Bank, Inc.', code: 'QCRB', account_format: 'Account Number' },
  { name: 'RANG-AY BANK A Rural Bank Inc', code: 'RANGAY', account_format: 'Account Number' },
  { name: 'Rizal Commercial Banking Corporation', code: 'RCBC', account_format: 'Account Number' },
  { name: 'Robinsons Bank Corporation', code: 'ROBINSONS', account_format: 'Account Number' },
  { name: 'Rural Bank of Guinobatan, Inc.', code: 'RBGI', account_format: 'Account Number' },
  { name: 'Seabank Philippines, Inc.', code: 'SEABANK', account_format: 'Account Number' },
  { name: 'Security Bank Corporation', code: 'SECBANK', account_format: 'Account Number' },
  { name: 'Security Bank Corporation 2', code: 'SECBANK2', account_format: 'Account Number' },
  { name: 'ShopeePay Philippines, Inc.', code: 'SHOPEEPAY', account_format: 'Mobile Number' },
  { name: 'SpeedyPay Inc.', code: 'SPEEDYPAY', account_format: 'Account Number' },
  { name: 'Standard Chartered Bank', code: 'SCB', account_format: 'Account Number' },
  { name: 'Starpay Corporation', code: 'STARPAY', account_format: 'Account Number' },
  { name: 'Sterling Bank of Asia, Inc., A Savings Bank', code: 'STERLING', account_format: 'Account Number' },
  { name: 'Sun Savings Bank, Inc.', code: 'SUNSB', account_format: 'Account Number' },
  { name: 'Tayocash Inc.', code: 'TAYOCASH', account_format: 'Mobile Number' },
  { name: 'TokTok Wallet Inc.', code: 'TOKTOK', account_format: 'Mobile Number' },
  { name: 'Tonik Digital Bank', code: 'TONIK', account_format: 'Account Number' },
  { name: 'Topjuan Tech Corporation', code: 'TOPJUAN', account_format: 'Account Number' },
  { name: 'Traxion Pay Inc.', code: 'TRAXION', account_format: 'Account Number' },
  { name: 'UCPB Savings Bank, Inc.', code: 'UCPBSB', account_format: 'Account Number' },
  { name: 'Union Bank of the Philippines', code: 'UNIONBANK', account_format: 'Account Number' },
  { name: 'Union Digital Bank', code: 'UNIONDIGITAL', account_format: 'Account Number' },
  { name: 'United Coconut Planters Bank', code: 'UCPB', account_format: 'Account Number' },
  { name: 'UnoBank Inc.', code: 'UNOBANK', account_format: 'Account Number' },
  { name: 'USSC Money Services, Inc', code: 'USSC', account_format: 'Account Number' },
  { name: 'Wealth Development Bank Corporation', code: 'WDBC', account_format: 'Account Number' },
  { name: 'Wise Pilipinas, Inc.', code: 'WISE', account_format: 'Account Number' },
  { name: 'Zybi Tech Inc.', code: 'ZYBI', account_format: 'Account Number' },
];

export default function AdminWithdrawals() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('requests'); // 'requests', 'batches', 'banks', or 'settings'
  
  // Banks state
  const [banks, setBanks] = useState([]);
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [bankForm, setBankForm] = useState({ name: '', code: '', account_format: 'Account Number', is_active: true });
  
  // Withdrawal Settings state
  const [withdrawalSettings, setWithdrawalSettings] = useState({
    minimum_amount: 500,
    maximum_amount: 50000,
    processing_fee: 0,
    processing_fee_type: 'fixed',
    enabled: true
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  
  // Withdrawals state
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalsTotal, setWithdrawalsTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [batchIdFilter, setBatchIdFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Batches state
  const [batches, setBatches] = useState([]);
  const [batchesTotal, setBatchesTotal] = useState(0);
  const [batchPage, setBatchPage] = useState(1);
  const [batchLimit, setBatchLimit] = useState(20);
  const [batchTotalPages, setBatchTotalPages] = useState(1);
  
  // Batch detail view state
  const [viewingBatch, setViewingBatch] = useState(null);
  const [batchWithdrawals, setBatchWithdrawals] = useState([]);
  const [batchInfo, setBatchInfo] = useState(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchBanks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/banks`, { headers });
      setBanks(res.data || []);
    } catch (err) {
      console.error('Failed to fetch banks:', err);
    }
  }, [headers]);

  const fetchWithdrawals = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const res = await axios.get(`${API}/admin/withdrawals?${params}`, { headers });
      let data = res.data.withdrawals || [];
      
      // Filter by batch ID client-side if specified
      if (batchIdFilter) {
        data = data.filter(w => w.batch_id && w.batch_id.toLowerCase().includes(batchIdFilter.toLowerCase()));
      }
      
      setWithdrawals(data);
      setWithdrawalsTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch withdrawals:', err);
    } finally {
      setIsLoading(false);
    }
  }, [headers, page, limit, statusFilter, batchIdFilter]);

  const fetchBatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/withdrawals/batches?page=${batchPage}&limit=${batchLimit}`, { headers });
      setBatches(res.data.batches || []);
      setBatchesTotal(res.data.total || 0);
      setBatchTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch batches:', err);
    }
  }, [headers, batchPage, batchLimit]);

  const fetchBatchDetail = useCallback(async (batchId) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/withdrawals/by-batch/${batchId}`, { headers });
      setBatchWithdrawals(res.data.withdrawals || []);
      setBatchInfo(res.data.batch || null);
    } catch (err) {
      console.error('Failed to fetch batch detail:', err);
      toast.error('Failed to load batch details');
    } finally {
      setIsLoading(false);
    }
  }, [headers]);

  const fetchWithdrawalSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/withdrawals/settings`, { headers });
      setWithdrawalSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch withdrawal settings:', err);
    }
  }, [headers]);

  useEffect(() => {
    if (token) {
      fetchBanks();
      fetchWithdrawals();
      fetchBatches();
      fetchWithdrawalSettings();
    }
  }, [token, fetchBanks, fetchWithdrawals, fetchBatches, fetchWithdrawalSettings]);

  useEffect(() => {
    if (viewingBatch) {
      fetchBatchDetail(viewingBatch);
    }
  }, [viewingBatch, fetchBatchDetail]);

  // Initialize default banks - adds missing banks (skips existing by code)
  const initializeDefaultBanks = async () => {
    setIsSaving(true);
    try {
      const response = await axios.post(`${API}/admin/banks/bulk`, DEFAULT_BANKS, { headers });
      if (response.data.added > 0) {
        toast.success(`Added ${response.data.added} banks${response.data.skipped > 0 ? `, skipped ${response.data.skipped} existing` : ''}`);
      } else {
        toast.info(`All ${response.data.skipped} banks already exist`);
      }
      fetchBanks();
    } catch (err) {
      console.error('Add banks error:', err);
      toast.error('Failed to add default banks: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBank = async () => {
    if (!bankForm.name || !bankForm.code) {
      toast.error('Name and code are required');
      return;
    }
    setIsSaving(true);
    try {
      if (editingBank) {
        await axios.put(`${API}/admin/banks/${editingBank.id}`, bankForm, { headers });
        toast.success('Bank updated');
      } else {
        await axios.post(`${API}/admin/banks`, bankForm, { headers });
        toast.success('Bank added');
      }
      setShowBankDialog(false);
      setEditingBank(null);
      setBankForm({ name: '', code: '', account_format: 'Account Number', is_active: true });
      fetchBanks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save bank');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteBank = async (bankId) => {
    if (!window.confirm('Delete this bank?')) return;
    try {
      await axios.delete(`${API}/admin/banks/${bankId}`, { headers });
      toast.success('Bank deleted');
      fetchBanks();
    } catch (err) {
      toast.error('Failed to delete bank');
    }
  };

  const handleSaveWithdrawalSettings = async () => {
    setIsSavingSettings(true);
    try {
      const params = new URLSearchParams({
        minimum_amount: withdrawalSettings.minimum_amount,
        maximum_amount: withdrawalSettings.maximum_amount,
        processing_fee: withdrawalSettings.processing_fee,
        processing_fee_type: withdrawalSettings.processing_fee_type,
        enabled: withdrawalSettings.enabled
      });
      await axios.put(`${API}/admin/withdrawals/settings?${params}`, {}, { headers });
      toast.success('Withdrawal settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleUpdateWithdrawalStatus = async (withdrawalId, newStatus) => {
    try {
      await axios.put(`${API}/admin/withdrawals/${withdrawalId}/status?status=${newStatus}`, {}, { headers });
      toast.success(`Status updated to ${newStatus}`);
      fetchWithdrawals();
      if (viewingBatch) {
        fetchBatchDetail(viewingBatch);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  // Export selected withdrawals as batch
  const handleExportBatch = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select withdrawals to export');
      return;
    }
    
    setIsExporting(true);
    try {
      const params = selectedIds.map(id => `withdrawal_ids=${id}`).join('&');
      const res = await axios.post(`${API}/admin/withdrawals/export-batch?${params}`, {}, { headers });
      
      // Download CSV file
      const blob = new Blob([res.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${res.data.batch_id}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Exported ${res.data.count} withdrawals as ${res.data.batch_id}`);
      setSelectedIds([]);
      fetchWithdrawals();
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export batch');
    } finally {
      setIsExporting(false);
    }
  };

  // Re-export existing batch as CSV
  const handleReExportBatch = async (batchId) => {
    setIsExporting(true);
    try {
      const res = await axios.get(`${API}/admin/withdrawals/batch/${batchId}/export`, { headers });
      
      // Download CSV file
      const blob = new Blob([res.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${res.data.batch_id}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded ${res.data.batch_id}.csv`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export batch');
    } finally {
      setIsExporting(false);
    }
  };

  // Update batch status
  const handleUpdateBatchStatus = async (batchId, newStatus) => {
    try {
      await axios.put(`${API}/admin/withdrawals/batch/${batchId}/status?status=${newStatus}`, {}, { headers });
      toast.success(`Batch ${batchId} updated to ${newStatus}`);
      fetchBatches();
      fetchWithdrawals();
      if (viewingBatch === batchId) {
        fetchBatchDetail(batchId);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update batch');
    }
  };

  // Toggle selection
  const toggleSelection = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Select all pending
  const selectAllPending = () => {
    const pendingIds = withdrawals.filter(w => w.status === 'pending').map(w => w.id);
    setSelectedIds(pendingIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedIds([]);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      processing: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    const icons = {
      pending: Clock,
      processing: Loader2,
      completed: CheckCircle,
      rejected: XCircle
    };
    const Icon = icons[status] || Clock;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return `₱${Number(amount || 0).toFixed(2)}`;
  };

  // Batch Detail View
  if (viewingBatch) {
    return (
      <div className="min-h-screen bg-stone-100">
        <AdminSidebar />
      <AdminMobileNav />
        
        <main className="md:ml-64 p-6 pt-16 md:pt-6 overflow-x-hidden">
          <div className="max-w-7xl mx-auto">
            {/* Back button and header */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
              <Button variant="outline" onClick={() => { setViewingBatch(null); setBatchWithdrawals([]); setBatchInfo(null); }}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Batches
              </Button>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-slate-900">Batch: {viewingBatch}</h1>
                {batchInfo && (
                  <p className="text-slate-500">
                    Created: {formatDate(batchInfo.batch_date)} | 
                    Status: {batchInfo.status} | 
                    Total: {formatAmount(batchInfo.total_amount)}
                  </p>
                )}
              </div>
              <Button 
                onClick={() => handleReExportBatch(viewingBatch)}
                disabled={isExporting}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <FileDown className="w-4 h-4 mr-2" />
                {isExporting ? 'Exporting...' : 'Export CSV'}
              </Button>
              {batchInfo?.status === 'processing' && (
                <>
                  <Button 
                    onClick={() => handleUpdateBatchStatus(viewingBatch, 'completed')}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Complete All
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={() => handleUpdateBatchStatus(viewingBatch, 'rejected')}
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Reject All
                  </Button>
                </>
              )}
            </div>

            {/* Batch withdrawals table */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                </div>
              ) : batchWithdrawals.length === 0 ? (
                <div className="p-12 text-center">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No withdrawals found in this batch</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Bank Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Account Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Account Number</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {batchWithdrawals.map(w => (
                      <tr key={w.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-mono text-slate-900">{w.reference}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-slate-900">{w.user?.name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">{w.user?.email}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">{w.bank?.name || w.payout_method}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{w.account_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{w.recipient_identifier}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-slate-900 text-right">{formatAmount(w.amount)}</td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(w.status)}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            {w.status === 'processing' && (
                              <>
                                <button
                                  onClick={() => handleUpdateWithdrawalStatus(w.id, 'completed')}
                                  className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                                >
                                  Complete
                                </button>
                                <button
                                  onClick={() => handleUpdateWithdrawalStatus(w.id, 'rejected')}
                                  className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      <AdminSidebar />
      <AdminMobileNav />
      
      <main className="md:ml-64 p-6 pt-16 md:pt-6 overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Withdrawal Requests</h1>
              <p className="text-slate-500">Manage withdrawal requests, batches, and bank options</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 flex-wrap">
            <Button
              variant={activeTab === 'requests' ? 'default' : 'outline'}
              onClick={() => setActiveTab('requests')}
              className={activeTab === 'requests' ? 'bg-slate-900' : ''}
              data-testid="tab-requests"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Withdrawal Requests
            </Button>
            <Button
              variant={activeTab === 'batches' ? 'default' : 'outline'}
              onClick={() => setActiveTab('batches')}
              className={activeTab === 'batches' ? 'bg-slate-900' : ''}
              data-testid="tab-batches"
            >
              <Package className="w-4 h-4 mr-2" />
              Batches
            </Button>
            <Button
              variant={activeTab === 'banks' ? 'default' : 'outline'}
              onClick={() => setActiveTab('banks')}
              className={activeTab === 'banks' ? 'bg-slate-900' : ''}
              data-testid="tab-banks"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Manage Banks
            </Button>
            <Button
              variant={activeTab === 'settings' ? 'default' : 'outline'}
              onClick={() => setActiveTab('settings')}
              className={activeTab === 'settings' ? 'bg-slate-900' : ''}
              data-testid="tab-settings"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Settings
            </Button>
          </div>

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold">Withdrawal Settings</h2>
                  <p className="text-sm text-slate-500">Configure withdrawal limits and fees</p>
                </div>
                <Button onClick={handleSaveWithdrawalSettings} disabled={isSavingSettings} data-testid="save-settings-btn">
                  {isSavingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Settings
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Minimum Amount */}
                <div className="space-y-2">
                  <Label htmlFor="min-amount">Minimum Withdrawal Amount (₱)</Label>
                  <Input
                    id="min-amount"
                    type="number"
                    min="0"
                    step="50"
                    value={withdrawalSettings.minimum_amount}
                    onChange={(e) => setWithdrawalSettings(prev => ({ ...prev, minimum_amount: parseFloat(e.target.value) || 0 }))}
                    data-testid="min-amount-input"
                  />
                  <p className="text-xs text-slate-500">Providers must have at least this amount to withdraw</p>
                </div>

                {/* Maximum Amount */}
                <div className="space-y-2">
                  <Label htmlFor="max-amount">Maximum Withdrawal Amount (₱)</Label>
                  <Input
                    id="max-amount"
                    type="number"
                    min="0"
                    step="1000"
                    value={withdrawalSettings.maximum_amount}
                    onChange={(e) => setWithdrawalSettings(prev => ({ ...prev, maximum_amount: parseFloat(e.target.value) || 0 }))}
                    data-testid="max-amount-input"
                  />
                  <p className="text-xs text-slate-500">Maximum amount per withdrawal request</p>
                </div>

                {/* Processing Fee */}
                <div className="space-y-2">
                  <Label htmlFor="processing-fee">Processing Fee</Label>
                  <div className="flex gap-2">
                    <Input
                      id="processing-fee"
                      type="number"
                      min="0"
                      step="0.5"
                      value={withdrawalSettings.processing_fee}
                      onChange={(e) => setWithdrawalSettings(prev => ({ ...prev, processing_fee: parseFloat(e.target.value) || 0 }))}
                      className="flex-1"
                      data-testid="processing-fee-input"
                    />
                    <Select value={withdrawalSettings.processing_fee_type} onValueChange={(value) => setWithdrawalSettings(prev => ({ ...prev, processing_fee_type: value }))}>
                      <SelectTrigger className="w-[140px]" data-testid="fee-type-select">
                        <SelectValue placeholder="Fee type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">₱ Fixed</SelectItem>
                        <SelectItem value="percentage">% Percentage</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-slate-500">Fee deducted from withdrawal amount</p>
                </div>

                {/* Enable/Disable */}
                <div className="space-y-2">
                  <Label>Withdrawals Enabled</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Switch
                      checked={withdrawalSettings.enabled}
                      onCheckedChange={(checked) => setWithdrawalSettings(prev => ({ ...prev, enabled: checked }))}
                      data-testid="enabled-switch"
                    />
                    <span className={withdrawalSettings.enabled ? 'text-green-600' : 'text-red-600'}>
                      {withdrawalSettings.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">Disable to prevent all new withdrawal requests</p>
                </div>
              </div>

              {/* Current Settings Summary */}
              <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                <h3 className="font-medium text-slate-900 mb-2">Current Settings Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Min Amount:</span>
                    <span className="ml-2 font-semibold">₱{withdrawalSettings.minimum_amount?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Max Amount:</span>
                    <span className="ml-2 font-semibold">₱{withdrawalSettings.maximum_amount?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Fee:</span>
                    <span className="ml-2 font-semibold">
                      {withdrawalSettings.processing_fee_type === 'fixed' 
                        ? `₱${withdrawalSettings.processing_fee}` 
                        : `${withdrawalSettings.processing_fee}%`}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Status:</span>
                    <span className={`ml-2 font-semibold ${withdrawalSettings.enabled ? 'text-green-600' : 'text-red-600'}`}>
                      {withdrawalSettings.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Banks Tab */}
          {activeTab === 'banks' && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Bank Options</h2>
                <div className="flex gap-2">
                  {banks.length === 0 && (
                    <Button variant="outline" onClick={initializeDefaultBanks} disabled={isSaving}>
                      Add Default Banks
                    </Button>
                  )}
                  <Button onClick={() => { setEditingBank(null); setBankForm({ name: '', code: '', account_format: 'Account Number', is_active: true }); setShowBankDialog(true); }} data-testid="add-bank-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Bank
                  </Button>
                </div>
              </div>

              {banks.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No banks configured yet</p>
                  <Button className="mt-4" onClick={initializeDefaultBanks} disabled={isSaving}>
                    {isSaving ? 'Adding...' : 'Add Default Philippine Banks'}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {banks.map(bank => (
                    <div 
                      key={bank.id} 
                      className={`p-4 rounded-xl border-2 ${bank.is_active ? 'border-slate-200' : 'border-red-200 bg-red-50/50'}`}
                      data-testid={`bank-${bank.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-slate-900">{bank.name}</h3>
                          <p className="text-xs text-slate-500">{bank.code}</p>
                          <p className="text-xs text-slate-400 mt-1">{bank.account_format}</p>
                        </div>
                        <div className="flex gap-1">
                          <button 
                            onClick={() => { setEditingBank(bank); setBankForm(bank); setShowBankDialog(true); }}
                            className="p-1.5 hover:bg-slate-100 rounded"
                          >
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </button>
                          <button 
                            onClick={() => handleDeleteBank(bank.id)}
                            className="p-1.5 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                      {!bank.is_active && (
                        <span className="text-xs text-red-600 mt-2 inline-block">Inactive</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Batches Tab */}
          {activeTab === 'batches' && (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-semibold">Export Batches</h2>
                <Button variant="outline" size="sm" onClick={fetchBatches}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </div>
              
              {batches.length === 0 ? (
                <div className="p-12 text-center">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No export batches yet</p>
                  <p className="text-sm text-slate-400 mt-1">Select pending withdrawals and click Export to create a batch</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Batch ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Batch Date</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Count</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Total Amount</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {batches.map(batch => (
                      <tr key={batch.id} className="hover:bg-slate-50" data-testid={`batch-${batch.id}`}>
                        <td className="px-4 py-3">
                          <p className="text-sm font-mono font-semibold text-slate-900">{batch.id}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{formatDate(batch.batch_date)}</td>
                        <td className="px-4 py-3 text-sm text-center text-slate-700">{batch.count}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-slate-900 text-right">
                          {formatAmount(batch.total_amount)}
                        </td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(batch.status)}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => setViewingBatch(batch.id)}
                              className="px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
                              data-testid={`batch-view-${batch.id}`}
                            >
                              <Eye className="w-3 h-3 inline mr-1" />
                              View
                            </button>
                            <button
                              onClick={() => handleReExportBatch(batch.id)}
                              className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200"
                              data-testid={`batch-export-${batch.id}`}
                            >
                              <FileDown className="w-3 h-3 inline mr-1" />
                              Export
                            </button>
                            {batch.status === 'processing' && (
                              <>
                                <button
                                  onClick={() => handleUpdateBatchStatus(batch.id, 'completed')}
                                  className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                                  data-testid={`batch-complete-${batch.id}`}
                                >
                                  Complete
                                </button>
                                <button
                                  onClick={() => handleUpdateBatchStatus(batch.id, 'rejected')}
                                  className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                                  data-testid={`batch-reject-${batch.id}`}
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Batch Pagination */}
              <TablePagination
                page={batchPage}
                totalPages={batchTotalPages}
                total={batchesTotal}
                limit={batchLimit}
                onPageChange={setBatchPage}
                onLimitChange={setBatchLimit}
              />
            </div>
          )}

          {/* Withdrawal Requests Tab */}
          {activeTab === 'requests' && (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {/* Filters & Export */}
              <div className="p-4 border-b space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-slate-500">Filter:</span>
                  {['all', 'pending', 'processing', 'completed', 'rejected'].map(status => (
                    <button
                      key={status}
                      onClick={() => { setStatusFilter(status); setPage(1); setSelectedIds([]); }}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        statusFilter === status ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`filter-${status}`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
                
                <div className="flex flex-wrap items-center gap-3">
                  {/* Batch ID Search */}
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search Batch ID..."
                      value={batchIdFilter}
                      onChange={e => setBatchIdFilter(e.target.value)}
                      className="w-48 h-9"
                      data-testid="batch-id-search"
                    />
                  </div>
                  
                  {/* Export Button - Only show for pending or all */}
                  <div className="flex items-center gap-2 ml-auto">
                    {selectedIds.length > 0 && (
                      <Button variant="outline" size="sm" onClick={clearSelection}>
                        Clear Selection
                      </Button>
                    )}
                    {withdrawals.filter(w => w.status === 'pending').length > 0 && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={selectAllPending}
                      >
                        Select All Pending ({withdrawals.filter(w => w.status === 'pending').length})
                      </Button>
                    )}
                    <Button 
                      onClick={handleExportBatch}
                      disabled={selectedIds.length === 0 || isExporting}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="export-batch-btn"
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      {isExporting ? 'Exporting...' : `Export CSV (${selectedIds.length})`}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Table */}
              {isLoading ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                </div>
              ) : withdrawals.length === 0 ? (
                <div className="p-12 text-center">
                  <CreditCard className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No withdrawal requests found</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left w-10">
                        <input
                          type="checkbox"
                          checked={selectedIds.length > 0 && selectedIds.length === withdrawals.filter(w => w.status === 'pending').length && withdrawals.filter(w => w.status === 'pending').length > 0}
                          onChange={e => {
                            if (e.target.checked) {
                              selectAllPending();
                            } else {
                              clearSelection();
                            }
                          }}
                          className="w-4 h-4 rounded border-slate-300"
                          disabled={withdrawals.filter(w => w.status === 'pending').length === 0}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Bank Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Account Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Account Number</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Batch ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {withdrawals.map(w => (
                      <tr key={w.id} className={`hover:bg-slate-50 ${selectedIds.includes(w.id) ? 'bg-blue-50' : ''}`} data-testid={`withdrawal-${w.id}`}>
                        <td className="px-4 py-3">
                          {w.status === 'pending' ? (
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(w.id)}
                              onChange={() => toggleSelection(w.id)}
                              className="w-4 h-4 rounded border-slate-300"
                              data-testid={`checkbox-${w.id}`}
                            />
                          ) : (
                            <span className="w-4 h-4 block"></span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-slate-900">{w.reference}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-slate-900">{w.user?.name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">{w.user?.email}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">{w.bank?.name || w.payout_method}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{w.account_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{w.recipient_identifier}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-slate-900 text-right">
                          {formatAmount(w.amount)}
                        </td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(w.status)}</td>
                        <td className="px-4 py-3">
                          {w.batch_id ? (
                            <button 
                              onClick={() => setViewingBatch(w.batch_id)}
                              className="text-xs font-mono bg-slate-100 px-2 py-1 rounded hover:bg-slate-200"
                            >
                              {w.batch_id}
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{formatDate(w.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            {w.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleUpdateWithdrawalStatus(w.id, 'processing')}
                                  className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                >
                                  Process
                                </button>
                                <button
                                  onClick={() => handleUpdateWithdrawalStatus(w.id, 'rejected')}
                                  className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {w.status === 'processing' && (
                              <button
                                onClick={() => handleUpdateWithdrawalStatus(w.id, 'completed')}
                                className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                              >
                                Complete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Pagination */}
              <TablePagination
                page={page}
                totalPages={totalPages}
                total={withdrawalsTotal}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            </div>
          )}
        </div>
      </main>

      {/* Bank Dialog */}
      <Dialog open={showBankDialog} onOpenChange={setShowBankDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingBank ? 'Edit Bank' : 'Add Bank'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Bank Name *</Label>
              <Input
                value={bankForm.name}
                onChange={e => setBankForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., BDO, GCash"
                className="mt-1.5"
                data-testid="bank-name-input"
              />
            </div>
            <div>
              <Label>Bank Code *</Label>
              <Input
                value={bankForm.code}
                onChange={e => setBankForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="e.g., BDO, GCASH"
                className="mt-1.5"
                data-testid="bank-code-input"
              />
            </div>
            <div>
              <Label>Account Format (Placeholder)</Label>
              <Input
                value={bankForm.account_format}
                onChange={e => setBankForm(p => ({ ...p, account_format: e.target.value }))}
                placeholder="e.g., Account Number, Mobile Number"
                className="mt-1.5"
              />
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
              <Switch
                checked={bankForm.is_active}
                onCheckedChange={checked => setBankForm(p => ({ ...p, is_active: checked }))}
              />
              <span className="text-sm font-medium">Active</span>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowBankDialog(false)}>
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
              <Button className="flex-1 bg-slate-900 hover:bg-slate-800" onClick={handleSaveBank} disabled={isSaving} data-testid="save-bank-btn">
                <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
